import { useToast } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ListNfseInput,
  ListNfseOutput,
  StatusTotalItem,
} from 'services/interfaces/nfse';
import * as nfseService from 'services/nfse';
import { onlyDocumentChars } from 'shared/helpers/string';
import NfseReport from './NfseReport';

export interface NfseReportFilters {
  startDateTime: string;
  endDateTime: string;
  status: string;
  referenceMonth: string;
  searchTerm: string;
}

export const detectSearchType = (
  value: string,
): {
  taxCode?: string;
  nbsCode?: string;
  document?: string;
  accessKey?: string;
} => {
  const cleanValue = onlyDocumentChars(value);
  const trimmedValue = value.trim();

  if (!trimmedValue) return {};

  if (trimmedValue.length === 6) {
    return { taxCode: trimmedValue };
  }
  if (trimmedValue.length === 9) {
    return { nbsCode: trimmedValue };
  }
  if (cleanValue.length === 11 || cleanValue.length === 14) {
    return { document: cleanValue };
  }
  if (trimmedValue.length >= 50) {
    return { accessKey: trimmedValue };
  }

  if (/^\d+$/.test(trimmedValue)) {
    return { document: trimmedValue };
  }

  return {};
};

interface NfseReportContextData {
  data: ListNfseOutput[];
  totalsByStatus: StatusTotalItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (page: number) => void;
  lastPage: number;
  filters: NfseReportFilters;
  setFilters: (filters: NfseReportFilters) => void;
  retransmitNfse: (orderId: number) => Promise<void>;
  retransmittingIds: number[];
  fetchData: () => Promise<void>;
  downloadDanfse: (accessKey: string) => Promise<void>;
  downloadingDanfseKeys: string[];
  downloadXml: (accessKey: string) => Promise<void>;
  downloadingXmlKeys: string[];
  sendEmail: (accessKey: string) => Promise<void>;
  sendingEmailKeys: string[];
}

const ITEMS_PER_PAGE = 20;

const getCurrentReferenceMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
};

const defaultFilters: NfseReportFilters = {
  startDateTime: '',
  endDateTime: '',
  status: '',
  referenceMonth: getCurrentReferenceMonth(),
  searchTerm: '',
};

const Context = createContext<NfseReportContextData>(
  {} as NfseReportContextData,
);

export const useNfseReportContext = () => useContext(Context);

export function buildListParams(
  filters: NfseReportFilters,
): Omit<ListNfseInput, 'limit' | 'offset'> {
  const params: ListNfseInput = {};

  if (filters.startDateTime) params.startDateTime = filters.startDateTime;
  if (filters.endDateTime) params.endDateTime = filters.endDateTime;
  if (filters.status) {
    params.status = filters.status.split(',').filter(Boolean);
  }
  if (filters.referenceMonth) params.referenceMonth = filters.referenceMonth;

  if (filters.searchTerm) {
    const searchParams = detectSearchType(filters.searchTerm);
    if (searchParams.taxCode) params.taxCode = searchParams.taxCode;
    if (searchParams.nbsCode) params.nbsCode = searchParams.nbsCode;
    if (searchParams.document) params.document = searchParams.document;
    if (searchParams.accessKey) params.accessKey = searchParams.accessKey;
  }

  return params;
}

const NfseReportContext = () => {
  const toast = useToast();
  const [data, setData] = useState<ListNfseOutput[]>([]);
  const [totalsByStatus, setTotalsByStatus] = useState<StatusTotalItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<NfseReportFilters>(defaultFilters);
  const [retransmittingIds, setRetransmittingIds] = useState<number[]>([]);
  const [downloadingDanfseKeys, setDownloadingDanfseKeys] = useState<string[]>(
    [],
  );
  const [downloadingXmlKeys, setDownloadingXmlKeys] = useState<string[]>([]);
  const [sendingEmailKeys, setSendingEmailKeys] = useState<string[]>([]);

  const lastPage = Math.ceil(total / ITEMS_PER_PAGE) || 1;

  const pageRef = useRef(page);
  pageRef.current = page;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    const currentPage = pageRef.current;
    const currentFilters = filtersRef.current;

    setLoading(true);
    setError(null);

    try {
      const params: ListNfseInput = {
        ...buildListParams(currentFilters),
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };

      const response = await nfseService.listNfse(params);

      setData(response.data.data);
      setTotal(response.data.total);
      setTotalsByStatus(response.data.totalsByStatus);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erro ao carregar dados do report';
      setError(message);
      toast({
        title: 'Erro',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const retransmitNfse = useCallback(
    async (orderId: number) => {
      if (!orderId) {
        toast({
          title: 'Erro',
          description: 'Order ID não disponível para retransmissão',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setRetransmittingIds((prev) => [...prev, orderId]);

      try {
        await nfseService.issueNfse(orderId);

        toast({
          title: 'Sucesso',
          description: 'NFS-e adicionada à fila de processamento',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Erro ao retransmitir NFS-e';
        toast({
          title: 'Erro',
          description: message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setRetransmittingIds((prev) => prev.filter((id) => id !== orderId));
      }
    },
    [toast],
  );

  const downloadDanfse = useCallback(
    async (accessKey: string) => {
      setDownloadingDanfseKeys((prev) => [...prev, accessKey]);

      try {
        const { data } = await nfseService.getDanfseNfse(accessKey);
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `danfse-${accessKey}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        let errorMessage = 'Não foi possível baixar a DANFSE';
        const axiosError = e as AxiosError;

        if (axiosError?.response?.data instanceof Blob) {
          try {
            const text = await axiosError.response.data.text();
            const parsed = JSON.parse(text);

            if (parsed.message == 'Request failed with status code 502') {
              errorMessage =
                'Erro ao baixar a DANFSE, tente novamente em alguns minutos';
            } else if (parsed.status == 404) {
              errorMessage = 'DANFSE não encontrada';
            } else {
              errorMessage = parsed.message || errorMessage;
            }
          } catch {
            console.error(
              'Erro ao baixar DANFSE:',
              axiosError?.response?.status,
              errorMessage,
            );
          }
        }

        toast({
          title: 'Erro',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setDownloadingDanfseKeys((prev) =>
          prev.filter((key) => key !== accessKey),
        );
      }
    },
    [toast],
  );

  const downloadXml = useCallback(
    async (accessKey: string) => {
      setDownloadingXmlKeys((prev) => [...prev, accessKey]);

      try {
        const { data } = await nfseService.getXmlNfse(accessKey);
        const blob = new Blob([data], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `nfse-${accessKey}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        toast({
          title: 'Erro',
          description: 'Não foi possível baixar o XML',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setDownloadingXmlKeys((prev) =>
          prev.filter((key) => key !== accessKey),
        );
      }
    },
    [toast],
  );

  const sendEmail = useCallback(
    async (accessKey: string) => {
      setSendingEmailKeys((prev) => [...prev, accessKey]);

      try {
        await nfseService.sendEmailNfse(accessKey);

        toast({
          title: 'Sucesso',
          description: 'E-mail enviado com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (e) {
        const axiosError = e as AxiosError;
        const statusCode = axiosError?.response?.status || 500;

        if (statusCode === 404) {
          toast({
            title: 'Erro',
            description: 'NFS-e não encontrada',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else if (statusCode === 400) {
          toast({
            title: 'Erro',
            description:
              'Erro ao enviar o e-mail, verifique se o e-mail é válido',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Erro',
            description:
              'Erro ao enviar o e-mail, tente novamente em alguns minutos',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setSendingEmailKeys((prev) => prev.filter((key) => key !== accessKey));
      }
    },
    [toast],
  );

  const prevFiltersRef = useRef(filters);

  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== filters;
    prevFiltersRef.current = filters;

    if (filtersChanged && page !== 1) {
      setPage(1);
      return;
    }

    fetchData();
  }, [page, filters, fetchData]);

  const contextValue: NfseReportContextData = {
    data,
    totalsByStatus,
    total,
    loading,
    error,
    page,
    setPage,
    lastPage,
    filters,
    setFilters,
    retransmitNfse,
    retransmittingIds,
    fetchData,
    downloadDanfse,
    downloadingDanfseKeys,
    downloadXml,
    downloadingXmlKeys,
    sendEmail,
    sendingEmailKeys,
  };

  return (
    <Context.Provider value={contextValue}>
      <NfseReport />
    </Context.Provider>
  );
};

export default NfseReportContext;
